import {
  fetchCampaignCreatorsQuery,
  fetchCampaignLabelsQuery,
  fetchCampaignModifiersQuery,
  fetchCampaignStatesQuery,
  fetchCampaignTypesQuery,
  fetchChannelTypesQuery,
  fetchListsWithParamsQuery,
  fetchMessageMediumsQuery,
  fetchPlacementsQuery,
} from 'api/queries';
import { WhatsApp } from 'constants/messageMediums';
import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
} from 'core/userContext';
import groupBy from 'lodash/groupBy';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { spacing } from 'ui/theme';
import { isString } from 'utils/helpers';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { useGraphql } from 'utils/hooks/useGraphql';
import noop from 'utils/noop';
import {
  ALERT_OPTIONS,
  CampaignFilterPrefix,
  campaignTypeMap,
  campaignTypeMapWithEligibility,
  messageMediumMap,
  TEMPLATE_TYPE_OPTIONS,
  WHATSAPP_STATUS_OPTIONS,
} from '../constants';
import { FilterOption, Filters } from '../types';
import { getFilterOptions } from '../utils';

const StyledMenu = styled(Menu)`
  [data-test="menu-list-container"] {
    min-width: ${spacing.x12};
  }
`;

export const content = {
  title: 'Filters',
};

const MAX_NO_FILTER_OPTIONS = 5;

type Props = {
  onSelect?: (values: string[][]) => void;
  selectedFilters?: Filters;
};

export const CampaignIndexFilterMenu = ({
  onSelect = noop,
  selectedFilters = [],
  ...props
}: Props) => {
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppFFOn, isWhatsAppGAEnabled: isWhatsAppGAFFOn } =
    useWhatsAppMessageChannelFeature();
  const resolvedCampaignTypeMap = isEmbeddedMessagingEnabled
    ? campaignTypeMapWithEligibility
    : campaignTypeMap;
  const isCampaignAlertsOn = useSelector(selectIsCampaignPerformanceAlertsFeatureFlagOn);

  // queries
  const { data: campaignLabelsData = {} } = useGraphql(fetchCampaignLabelsQuery);
  const { data: messageMediumData = {} } = useGraphql(fetchMessageMediumsQuery);
  const { data: campaignTypesData = {} } = useGraphql(fetchCampaignTypesQuery);
  const { data: channelTypesData = {} } = useGraphql(fetchChannelTypesQuery);
  const { data: listsData = {} } = useGraphql(fetchListsWithParamsQuery, {
    variables: { pagination: { limit: 10000, offset: 0 } },
  });
  const { data: campaignStatesData = {} } = useGraphql(fetchCampaignStatesQuery);
  const { data: campaignCreatorsData = {} } = useGraphql(fetchCampaignCreatorsQuery);
  const { data: campaignModifiersData = {} } = useGraphql(fetchCampaignModifiersQuery);
  const { data: placementsData = {} } = useGraphql(fetchPlacementsQuery);

  // format query data to readable arrays
  const campaignLabels = campaignLabelsData?.campaignLabels || [];
  const messageMediums =
    messageMediumData?.messageMediums
      ?.filter((med: string) => isWhatsAppFFOn || med !== WhatsApp)
      ?.map((medium: string) => ({
        id: medium,
        label: messageMediumMap[medium],
      })) || [];
  const campaignTypes =
    campaignTypesData?.campaignTypes?.map((type: keyof typeof resolvedCampaignTypeMap) => ({
      id: type,
      label: resolvedCampaignTypeMap[type],
    })) || [];
  const channelTypes = channelTypesData?.channelTypes || [];
  const lists = listsData?.lists?.results || [];
  const campaignStates = campaignStatesData?.campaignStates || [];
  const campaignCreators = campaignCreatorsData?.campaignCreators || [];
  const campaignModifiers = campaignModifiersData?.campaignModifiers || [];
  const placementOptions =
    placementsData?.placements?.map(
      (placement: {
        payload: { id: number; fields: { placementName: string } };
      }) => ({
        id: placement?.payload?.id,
        label: placement?.payload?.fields?.placementName,
      }),
    ) || [];

  // combine into object for other transformations
  const queriedOptions: { [key: string]: any } = {
    [CampaignFilterPrefix.Label]: campaignLabels,
    [CampaignFilterPrefix.Medium]: messageMediums,
    [CampaignFilterPrefix.CampaignType]: campaignTypes,
    [CampaignFilterPrefix.ChannelType]: channelTypes,
    [CampaignFilterPrefix.AudienceList]: lists,
    [CampaignFilterPrefix.Status]: campaignStates,
    ...(isCampaignAlertsOn && { [CampaignFilterPrefix.Alerts]: ALERT_OPTIONS }),
    [CampaignFilterPrefix.Creator]: campaignCreators,
    [CampaignFilterPrefix.ModifiedBy]: campaignModifiers,
    ...(isWhatsAppFFOn && {
      [CampaignFilterPrefix.TemplateType]: TEMPLATE_TYPE_OPTIONS,
      ...(isWhatsAppGAFFOn && {
        [CampaignFilterPrefix.TemplateStatus]: WHATSAPP_STATUS_OPTIONS(true),
      }),
    }),
    ...(isEmbeddedMessagingEnabled && { [CampaignFilterPrefix.Placements]: placementOptions }),
  };

  // Prefix options to prevent duplication
  const builtFilterOptions = getFilterOptions(
    isEmbeddedMessagingEnabled,
    isCampaignAlertsOn,
    isWhatsAppFFOn,
  ).map(({ id, label, forMedium, prefix, queryId, queryLabel }) => ({
    id,
    label,
    isSelectMenu: true,
    forMedium,
    prefix,
    options:
      queriedOptions[prefix]?.map((secondaryOption: string | Record<string, string>) => {
        const isSecondaryOptionAString = isString(secondaryOption);

        if (isSecondaryOptionAString) {
          return {
            label: secondaryOption,
            id: `${prefix}${secondaryOption}`,
          };
        }

        const label = secondaryOption[queryLabel as string];
        const id = secondaryOption[queryId as string];
        const options = secondaryOption['options'];
        const isExpandableGroup = secondaryOption['isExpandableGroup'];
        const childPrefix = secondaryOption['prefix'];

        return {
          // Assigns value from query to label
          // this label and id will vary from query to query, set in constants
          // for locales it would be something like instead of queryLabel languageName, and id would be tag

          label,
          id: `${prefix}${id}`,
          options,
          isExpandableGroup,
          prefix: childPrefix,
        };
      }) ?? [],
  }));

  // Build Id Maps, an object of all ids by filter category and children
  const filterOptionIdsMap = builtFilterOptions.reduce(
    (
      acc: { [key: string]: string[] },
      {
        prefix,
        options,
      }: { prefix: string; options: { id: string; prefix: string; options?: { id: string }[] }[] },
    ) => {
      const childOptions = options.filter(({ options }) => options?.length);

      const updatedMap = childOptions.reduce(
        (childAcc, { prefix: childPrefix, options: childOptions }) => {
          const childIds = childOptions?.map(({ id }) => id) || [];

          return {
            ...childAcc,
            [childPrefix]: [...(childAcc[childPrefix] || []), ...childIds],
          };
        },
        { [prefix]: [...(acc[prefix] || []), ...options.map(({ id }) => id)] },
      );

      return {
        ...acc,
        ...updatedMap,
      };
    },
    {},
  );

  const parentChildPrefixMap = builtFilterOptions.reduce(
    (
      acc: Record<string, string[]>,
      {
        prefix,
        options,
      }: { prefix: string; options: { id: string; prefix: string; options?: { id: string }[] }[] },
    ) => {
      const childPrefixes = options
        .filter(
          ({ options, prefix: childPrefix }) =>
            options?.length && childPrefix && childPrefix !== prefix,
        )
        .map(({ prefix: childPrefix }) => childPrefix);

      if (childPrefixes.length > 0) {
        acc[prefix] = [...(acc[prefix] || []), ...childPrefixes];
      }
      return acc;
    },
    {},
  );

  const childrenFilterOptionIdsMap: { [key: string]: string[] } = builtFilterOptions.reduce(
    (acc, { options }: { options: { id: string; options?: { id: string }[] }[] }) => {
      const childOptionsMap = options.reduce((childAcc, { id, options: childOptions }) => {
        if (childOptions?.length) {
          return { ...childAcc, [id]: childOptions.map(({ id }) => id) };
        }
        return childAcc;
      }, {});
      return { ...acc, ...childOptionsMap };
    },
    {},
  );

  // Use Maps to calculate totals by filter category and transform into an object with totals by key
  const filterTotals: { [key: string]: number } = Object.keys(filterOptionIdsMap).reduce(
    (acc, key) => ({
      ...acc,
      [key]: filterOptionIdsMap[key].reduce(
        (total: number, filterId: string) =>
          selectedFilters.includes(filterId) ? total + 1 : total,
        0,
      ),
    }),
    {},
  );

  // Add totals to get full total
  const allFiltersTotal = Object.values(filterTotals).reduce(
    (total, filterTotal) => total + filterTotal,
    0,
  );

  // Add totals to options
  const builtFilterOptionsWithTotals = builtFilterOptions
    .map((option) => {
      const childTotal =
        parentChildPrefixMap[option.prefix]?.reduce(
          (acc, childPrefix) => acc + (filterTotals[childPrefix] || 0),
          0,
        ) || 0;

      return {
        ...option,
        hasFilter: option.options.length > MAX_NO_FILTER_OPTIONS,
        hasCountBadge: (filterTotals[option.prefix] || 0) + childTotal > 0,
      };
    })
    .filter(
      (filter) =>
        filter.options.length > 0 &&
        !Object.keys(parentChildPrefixMap).some((parentPrefix) =>
          parentChildPrefixMap[parentPrefix]?.includes(filter.prefix),
        ),
    );

  const groupedByMedium = groupBy(builtFilterOptionsWithTotals, 'forMedium');

  const builtFilterOptionsWithTotalsAndGroups =
    Object.keys(groupedByMedium).length > 1
      ? Object.keys(groupedByMedium).reduce(
          (builtOptions: any, forMedium, index) => [
            ...builtOptions,
            ...(index !== 0
              ? [
                  {
                    isDivider: true,
                    id: `medium-divider-${index}`,
                    label: '',
                  },
                ]
              : []),
            {
              isDisabled: true,
              isTitle: true,
              id: `medium-label-${index}`,
              label: forMedium,
            },
            ...groupedByMedium[forMedium],
          ],
          [],
        )
      : builtFilterOptionsWithTotals;

  const handleSelection = (selectedId: string) => {
    const selectedIdIndex = selectedFilters.indexOf(selectedId);
    const childrenOptions = childrenFilterOptionIdsMap[selectedId] ?? [];
    let newSelectedFilters: FilterOption[];

    if (selectedIdIndex !== -1) {
      // Remove the selectedId and its children or the parent if selected
      newSelectedFilters = selectedFilters.filter(
        (filter) =>
          filter !== selectedId &&
          !childrenOptions.includes(filter as string) &&
          !Object.keys(childrenFilterOptionIdsMap).some(
            (parentId) =>
              parentId === filter && childrenFilterOptionIdsMap[parentId].includes(selectedId),
          ),
      );
    } else {
      // Add the selectedId and its children
      newSelectedFilters = [
        ...selectedFilters,
        selectedId,
        ...childrenOptions.filter((childOption) => !selectedFilters.some((o) => childOption === o)),
      ];

      // Check if all children of a parent are selected, and add the parent if necessary
      Object.keys(childrenFilterOptionIdsMap).forEach((parentId) => {
        const childOptions = childrenFilterOptionIdsMap[parentId];
        if (
          !newSelectedFilters.some((o) => o === parentId) &&
          childOptions.every((option) => newSelectedFilters.includes(option))
        ) {
          newSelectedFilters = [...newSelectedFilters, parentId];
        }
      });
    }

    const options = getFilterOptions(
      isEmbeddedMessagingEnabled,
      isCampaignAlertsOn,
      isWhatsAppFFOn,
    );

    // returns an 2d array mirroring the order of the filters, while filtering out stale filters
    const selected = options.map((o) =>
      newSelectedFilters.filter((selection) =>
        filterOptionIdsMap[o.prefix].includes(selection as string),
      ),
    ) as string[][];

    onSelect(selected);
  };

  return (
    <StyledMenu
      options={builtFilterOptionsWithTotalsAndGroups}
      defaultSelectedOptionIds={selectedFilters as string[]}
      handleSelectionChange={handleSelection}
      horizontalAlignment={ALIGNMENT.LEFT}
      {...props}
    >
      <FilterMenuTriggerButton selectedFilterCount={allFiltersTotal} />
    </StyledMenu>
  );
};
